/* =========================================================
   IMRC VIP Pre-Order + Quantity-Based Stock Logic
   Asset: vip-preorder-quantity.js
   Version: v4

   Rules:
   - Stock available and requested quantity <= stock: Add to cart
   - Requested quantity exceeds stock, OR stock is 0/below:
       - B2B-VIP customer + inventory_policy continue: Pre-Order
       - Everyone else: Sold Out
   - Displays In stock quantity beside the quantity label.

   v4 fix:
   Dawn/product-info.js can re-render the Add to cart button after a
   variant change because Shopify sees "continue selling" as available.
   This file now watches for those DOM changes and re-applies the IMRC
   rule after Shopify finishes updating the variant.
========================================================= */
(function () {
  var applyTimer = null;
  var isApplying = false;
  var observerStarted = false;

  function toNumber(value, fallback) {
    var num = parseInt(value, 10);
    return Number.isFinite(num) ? num : fallback;
  }

  function getButtonTextElement(button) {
    if (!button) return null;

    var span = button.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
    return span || button;
  }

  function setButton(button, text, disabled) {
    if (!button) return;

    var textElement = getButtonTextElement(button);
    if (textElement && textElement.textContent.trim() !== text) {
      textElement.textContent = text;
    }

    if (disabled) {
      if (!button.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('disabled');
    } else {
      if (button.hasAttribute('disabled')) button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('disabled');
    }
  }

  function getDataForSection(sectionId) {
    var script = document.getElementById('VipPreorderData-' + sectionId);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[IMRC VIP Pre-Order] Could not parse variant data.', error);
      return null;
    }
  }

  function getSelectedVariantId(productInfo) {
    var idInput = productInfo.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);

    var selectedVariantJson = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (selectedVariantJson && selectedVariantJson.textContent) {
      try {
        var variant = JSON.parse(selectedVariantJson.textContent);
        if (variant && variant.id) return String(variant.id);
      } catch (error) {}
    }

    return '';
  }

  function findVariant(data, variantId) {
    if (!data || !Array.isArray(data.variants)) return null;
    return data.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    }) || null;
  }

  function getSubmitButton(productInfo, sectionId) {
    return (
      productInfo.querySelector('#ProductSubmitButton-' + sectionId) ||
      productInfo.querySelector('button[name="add"].product-form__submit') ||
      productInfo.querySelector('button[name="add"]')
    );
  }

  function toggleDynamicCheckout(productInfo, hide) {
    var paymentButton = productInfo.querySelector('.shopify-payment-button');
    if (!paymentButton) return;
    paymentButton.style.display = hide ? 'none' : '';
  }

  function setPreorderProperty(productInfo, isPreorder) {
    var form = productInfo.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var input = form.querySelector('input[name="properties[Pre-Order]"]');
    if (isPreorder) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Pre-Order]';
        form.appendChild(input);
      }
      input.value = 'Yes';
    } else if (input) {
      input.remove();
    }
  }

  function updateOneProduct(productInfo) {
    if (!productInfo) return;

    var sectionId = productInfo.dataset.section;
    if (!sectionId) return;

    var data = getDataForSection(sectionId);
    var variantId = getSelectedVariantId(productInfo);
    var variant = findVariant(data, variantId);
    var quantityInput = productInfo.querySelector('.quantity__input');
    var requestedQty = toNumber(quantityInput && quantityInput.value, 1);
    var button = getSubmitButton(productInfo, sectionId);
    var stockLabel = productInfo.querySelector('#VipStockQty-' + sectionId);

    if (!data || !variant || !button) return;

    var inventoryManagement = variant.inventory_management;
    var tracksInventory = inventoryManagement === 'shopify';
    var stockQtyRaw = toNumber(variant.inventory_quantity, 0);
    var stockQtyDisplay = Math.max(0, stockQtyRaw);
    var continueSelling = variant.inventory_policy === 'continue';
    var isB2bVip = data.customer_is_b2b_vip === true;

    var isOverStock = tracksInventory && requestedQty > stockQtyRaw;
    var isZeroOrBelow = tracksInventory && stockQtyRaw <= 0;
    var shouldPreorder = tracksInventory && continueSelling && isB2bVip && (isOverStock || isZeroOrBelow);
    var shouldBlock = tracksInventory && (isOverStock || isZeroOrBelow) && !shouldPreorder;
    var normalAvailable = !tracksInventory || (stockQtyRaw > 0 && requestedQty <= stockQtyRaw);

    if (stockLabel) {
      if (tracksInventory) {
        stockLabel.hidden = false;
        stockLabel.classList.toggle('imrc-vip-stock-qty--preorder', shouldPreorder);

        if (shouldPreorder && requestedQty > stockQtyDisplay) {
          var preorderQty = requestedQty - stockQtyDisplay;
          stockLabel.textContent = 'In stock: ' + stockQtyDisplay + ' • Pre-order: ' + preorderQty;
        } else {
          stockLabel.textContent = 'In stock: ' + stockQtyDisplay;
        }
      } else {
        stockLabel.hidden = true;
        stockLabel.textContent = '';
      }
    }

    if (shouldPreorder) {
      setButton(button, 'Pre-Order', false);
      toggleDynamicCheckout(productInfo, true);
      setPreorderProperty(productInfo, true);
      return;
    }

    if (shouldBlock) {
      setButton(button, 'Sold Out', true);
      toggleDynamicCheckout(productInfo, true);
      setPreorderProperty(productInfo, false);
      return;
    }

    if (normalAvailable) {
      setButton(
        button,
        window.variantStrings && window.variantStrings.addToCart ? window.variantStrings.addToCart : 'Add to cart',
        false
      );
      toggleDynamicCheckout(productInfo, false);
      setPreorderProperty(productInfo, false);
      return;
    }

    setButton(button, 'Sold Out', true);
    toggleDynamicCheckout(productInfo, true);
    setPreorderProperty(productInfo, false);
  }

  function updateAllProducts() {
    isApplying = true;
    try {
      document.querySelectorAll('product-info').forEach(updateOneProduct);
    } finally {
      window.setTimeout(function () {
        isApplying = false;
      }, 0);
    }
  }

  function updateSoon() {
    if (applyTimer) window.clearTimeout(applyTimer);
    applyTimer = window.setTimeout(updateAllProducts, 25);
  }

  function updateAfterShopifyRender() {
    updateSoon();
    window.setTimeout(updateAllProducts, 75);
    window.setTimeout(updateAllProducts, 175);
    window.setTimeout(updateAllProducts, 375);
    window.setTimeout(updateAllProducts, 850);
  }

  function startObserver() {
    if (observerStarted || !window.MutationObserver) return;
    observerStarted = true;

    var observer = new MutationObserver(function (mutations) {
      if (isApplying) return;

      var shouldUpdate = mutations.some(function (mutation) {
        if (mutation.type === 'childList') return true;
        if (mutation.type === 'attributes') {
          return ['disabled', 'aria-disabled', 'class', 'value'].indexOf(mutation.attributeName) !== -1;
        }
        if (mutation.type === 'characterData') return true;
        return false;
      });

      if (shouldUpdate) updateSoon();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class', 'value']
    });
  }

  window.IMRCUpdateVipPreorderQuantity = updateAfterShopifyRender;

  document.addEventListener('DOMContentLoaded', function () {
    startObserver();
    updateAfterShopifyRender();
  });

  document.addEventListener('product-info:loaded', updateAfterShopifyRender);

  document.addEventListener('change', function (event) {
    if (event.target.matches('.quantity__input, input[name="id"], select')) updateAfterShopifyRender();
  });

  document.addEventListener('input', function (event) {
    if (event.target.matches('.quantity__input')) updateAfterShopifyRender();
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('.quantity__button, variant-selects, .product-form__input, .product-form__input input, .product-form__input label')) {
      updateAfterShopifyRender();
    }
  });

  // Dawn's pub/sub object is not always exposed on window in every theme build,
  // but use it when it is available.
  if (window.PUB_SUB_EVENTS && window.subscribe && window.PUB_SUB_EVENTS.variantChange) {
    window.subscribe(window.PUB_SUB_EVENTS.variantChange, updateAfterShopifyRender);
  }

  // Start observer even if this asset loads after DOMContentLoaded.
  if (document.readyState !== 'loading') {
    startObserver();
    updateAfterShopifyRender();
  }
})();
